import * as i0 from '@angular/core';
import { Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

var E11CardState;
(function (E11CardState) {
    E11CardState["Default"] = "default";
    E11CardState["Valid"] = "valid";
    E11CardState["Invalid"] = "invalid";
    E11CardState["Disabled"] = "disabled";
})(E11CardState || (E11CardState = {}));
var E11CardArrowPosition;
(function (E11CardArrowPosition) {
    E11CardArrowPosition["Top"] = "top";
    E11CardArrowPosition["Bottom"] = "bottom";
})(E11CardArrowPosition || (E11CardArrowPosition = {}));

/**
 * A card component that can be used to display content in a card format.
 * It has different states that can be used to indicate the status of the card.
 * @example
 * <e11-card>Content</e11-card>
 * @example
 * <e11-card [state]="E11CardState.Valid">Content</e11-card>
 * @example
 * <e11-card [state]="E11CardState.Invalid">Content</e11-card> <e11-card [state]="E11CardState.Disabled">Content</e11-card>
 * @example
 * <e11-card [isClickable]="false">Content</e11-card>
 */
class E11CardComponent {
    constructor() {
        this.cardStateEnum = E11CardState;
        /**
         * Indicates if the card is clickable. When set to false, the hover state is disabled making the card appear non-actionable.
         * @type {boolean}
         * @default true
         */
        this.isClickable = true;
        /**
         * Toggles the 'active' class on the card.
         * @type {boolean}
         * @default false
         */
        this.status = false;
        /**
         * Sets the state of the card.
         * @type {E11CardState}
         * @default E11CardState.Default
         */
        this.state = E11CardState.Default;
        // TODO - Remove in the next major release
        /**
         * Determines the position of the arrow on the card. Accepted values are 'top' or 'bottom'.
         * @type {string}
         * @deprecated This property is deprecated.
         * @default E11CardArrowPosition.Top
         */
        this.arrow = E11CardArrowPosition.Top;
        /**
         * If true, the border of the card will be dashed.
         * @type {boolean}
         * @default false
         */
        this.borderDashed = false;
        /**
         * Sets the width of the card. The value can be a string in px or percentage.
         * @type {string}
         * @default '100%'
         */
        this.width = '100%';
        /**
         * Sets the height of the card. The value can be a string in px or percentage.
         * @type {string}
         * @default '100%'
         */
        this.height = '100%';
        /**
         * Indicates if the card is in a loading state.
         * @type {boolean}
         * @default false
         */
        this.isLoading = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11CardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11CardComponent, isStandalone: true, selector: "e11-card", inputs: { isClickable: "isClickable", status: "status", state: "state", arrow: "arrow", borderDashed: "borderDashed", width: "width", height: "height", containerClassOverrides: "containerClassOverrides", containerClass: "containerClass", customStateCss: "customStateCss", isLoading: "isLoading" }, ngImport: i0, template: ` <div
    class="e11-card e11-w-full e11-border e11-rounded-lg e11-p-4 {{ containerClassOverrides }} {{ containerClass }}  {{ customStateCss }} "
    [ngClass]="{
      'e11-card-active': status,
      'hover:e11-cursor-pointer hover:e11-shadow': isClickable && state !== cardStateEnum.Disabled,
      'hover:e11-bg-gray-50': isClickable && !status,
      'e11-border-skin-app-borders': !customStateCss && state === cardStateEnum.Default,
      'e11-border-skin-success e11-bg-green-50': !customStateCss && state === cardStateEnum.Valid,
      'e11-border-skin-error e11-bg-red-50': !customStateCss && state === cardStateEnum.Invalid,
      'e11-border-skin-grey e11-bg-grey-50 e11-cursor-not-allowed e11-opacity-50': !customStateCss && state === cardStateEnum.Disabled,
      'e11-border-dashed': borderDashed,
      'e11-animate-pulse e11-bg-skin-grey/20': isLoading,

    }"
    [ngStyle]="{ width: width, height: height }"
  >
    <ng-content></ng-content>
  </div>`, isInline: true, styles: [".e11-card:after{top:100%;left:50%;border:solid transparent;content:\"\";height:0;width:0;position:absolute;pointer-events:none;border-width:20px;margin-left:-20px}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11CardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-card', standalone: true, imports: [CommonModule], encapsulation: ViewEncapsulation.None, template: ` <div
    class="e11-card e11-w-full e11-border e11-rounded-lg e11-p-4 {{ containerClassOverrides }} {{ containerClass }}  {{ customStateCss }} "
    [ngClass]="{
      'e11-card-active': status,
      'hover:e11-cursor-pointer hover:e11-shadow': isClickable && state !== cardStateEnum.Disabled,
      'hover:e11-bg-gray-50': isClickable && !status,
      'e11-border-skin-app-borders': !customStateCss && state === cardStateEnum.Default,
      'e11-border-skin-success e11-bg-green-50': !customStateCss && state === cardStateEnum.Valid,
      'e11-border-skin-error e11-bg-red-50': !customStateCss && state === cardStateEnum.Invalid,
      'e11-border-skin-grey e11-bg-grey-50 e11-cursor-not-allowed e11-opacity-50': !customStateCss && state === cardStateEnum.Disabled,
      'e11-border-dashed': borderDashed,
      'e11-animate-pulse e11-bg-skin-grey/20': isLoading,

    }"
    [ngStyle]="{ width: width, height: height }"
  >
    <ng-content></ng-content>
  </div>`, styles: [".e11-card:after{top:100%;left:50%;border:solid transparent;content:\"\";height:0;width:0;position:absolute;pointer-events:none;border-width:20px;margin-left:-20px}\n"] }]
        }], propDecorators: { isClickable: [{
                type: Input
            }], status: [{
                type: Input
            }], state: [{
                type: Input
            }], arrow: [{
                type: Input
            }], borderDashed: [{
                type: Input
            }], width: [{
                type: Input
            }], height: [{
                type: Input
            }], containerClassOverrides: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], customStateCss: [{
                type: Input
            }], isLoading: [{
                type: Input
            }] } });

class E11CardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11CardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11CardModule, imports: [E11CardComponent], exports: [E11CardComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11CardModule, imports: [E11CardComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11CardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11CardComponent],
                    exports: [E11CardComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11CardArrowPosition, E11CardComponent, E11CardModule, E11CardState };
